// src/components/HomePage.js - With Firebase Cart
import React, { useState, useEffect, useMemo } from "react";
import { Container, Box, Alert, Button } from "@mui/material";
import { signOut, deleteUser, reauthenticateWithPopup } from "firebase/auth";
import {
  doc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase/config";
import CheckoutPage from "./CheckoutPage";
import MyOrders from "./MyOrders";
import ProfileSettings from "./ProfileSettings";
import PackageStore from "./PackageStore";
import ItemCheckoutPage from "./ItemCheckoutPage";
import CartPage from "./CartPage";
import WalletView from "./WalletView";

// Import the new components
import NavBar from "./NavBar";
import CartDrawer from "./CartDrawer";
import UserMenu from "./UserMenu";
import ProductGrid from "./ProductGrid";
import Footer from "./Footer";
import FloatingCartButton from "./FloatingCartButton";
import Notifications from "./Notifications";
// Import Firebase Cart Service
import firebaseCartService from "../services/FirebaseCartService";
import { shuffleArray } from "../utils/arrayUtils";
import { ItemRequestService } from "../services/ItemRequestService";
import { ADMIN_CONFIG } from "../config/appConfig";
import UltraCustomerPopup from "./UltraCustomerPopup";
import MilestoneRewardPopup from "./MilestoneRewardPopup";
import {
  checkMilestonePopupPending,
  getMilestoneDetails,
  notifyMilestoneAchievement,
  markMilestonePopupShown,
  markMilestoneEmailSent,
} from "../services/MilestoneService";
import {
  ALL_CATEGORIES_LABEL,
  NAV_CATEGORIES,
  canonicalCategoryName,
  categoryMatchesFilter,
} from "../config/productCategories";
import CategoryIconStrip from "./CategoryIconStrip";
import CategoryComingSoonPanel from "./CategoryComingSoonPanel";
import {
  brandPalette as primaryBlue,
  brandGlassStyles as glassStyles,
  brandCardGlassStyles as cardGlassStyles,
  getBrandPageBackground,
} from "../config/brandTheme";

const HomePage = ({ user, onSignOut, onAdminPanel }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showPackageStore, setShowPackageStore] = useState(false);
  const [showItemCheckout, setShowItemCheckout] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const [showWallet, setShowWallet] = useState(false);
  const [showUltraPopup, setShowUltraPopup] = useState(false);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_LABEL);
  const [categories] = useState(NAV_CATEGORIES);

  // Pagination and lazy loading states
  const [displayedItemsCount, setDisplayedItemsCount] = useState(8);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);

  // Cart state - now managed by Firebase
  const [cart, setCart] = useState({
    packages: [],
    items: [],
  });
  const [cartLoading, setCartLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const isAdmin = ADMIN_CONFIG.isAdmin(user.email);

  // Initialize Firebase cart when user is available
  useEffect(() => {
    if (user?.uid) {
      initializeFirebaseCart();

      // Set up real-time cart listener
      const unsubscribe = firebaseCartService.setupCartListener(
        user.uid,
        (updatedCart) => {
          console.log("📡 Real-time cart update:", updatedCart);
          setCart(updatedCart);
        }
      );

      // Cleanup listener on unmount or user change
      return () => {
        if (unsubscribe) {
          firebaseCartService.removeCartListener(user.uid);
        }
      };
    }
  }, [user?.uid]);

  // Initialize Firebase cart (load existing cart or migrate from localStorage)
  const initializeFirebaseCart = async () => {
    if (!user?.uid) return;

    try {
      setCartLoading(true);
      console.log("🚀 Initializing Firebase cart for user:", user.uid);

      // Check for existing localStorage cart to migrate
      let localStorageCart = null;
      try {
        const savedLocalCart = localStorage.getItem(
          `infinitywhale_cart_${user.uid}`
        );
        if (savedLocalCart) {
          const parsed = JSON.parse(savedLocalCart);
          if (parsed.packages || parsed.items) {
            localStorageCart = {
              packages: parsed.packages || [],
              items: parsed.items || [],
            };
            console.log(
              "📦 Found localStorage cart to migrate:",
              localStorageCart
            );
          }
        }
      } catch (e) {
        console.log("⚠️ No valid localStorage cart found");
      }

      // Load or migrate cart
      const result = await firebaseCartService.migrateLocalStorageCart(
        user.uid,
        localStorageCart
      );

      if (result.success) {
        setCart(result.cart);
        console.log("✅ Cart initialized successfully:", result.cart);
      } else {
        console.error("❌ Failed to initialize cart:", result.error);
        setError("Failed to load your cart. Please refresh the page.");
      }
    } catch (error) {
      console.error("❌ Error initializing Firebase cart:", error);
      setError("Failed to load your cart. Please refresh the page.");
    } finally {
      setCartLoading(false);
    }
  };

  // Cart functions - now using Firebase
  const addPackageToCart = async (packageData) => {
    if (!user?.uid) {
      setSnackbar({
        open: true,
        message: "Please sign in to add items to cart",
      });
      return;
    }

    try {
      setCartLoading(true);
      const result = await firebaseCartService.addPackageToCart(
        user.uid,
        packageData
      );

      if (result.success) {
        setSnackbar({ open: true, message: result.message });
        setCartDrawerOpen(true);
        // Cart will be updated via real-time listener
      } else {
        setSnackbar({ open: true, message: result.error });
      }
    } catch (error) {
      console.error("❌ Error adding package to cart:", error);
      setSnackbar({ open: true, message: "Failed to add package to cart" });
    } finally {
      setCartLoading(false);
    }
  };

  const addItemToCart = async (itemData, quantity = 1) => {
    if (!user?.uid) {
      setSnackbar({
        open: true,
        message: "Please sign in to add items to cart",
      });
      return;
    }

    // Debug logging
    console.log("🛒 Add to cart attempt:", {
      itemId: itemData.id,
      itemName: itemData.name,
      stockQuantity: itemData.stockQuantity,
      maxOrderQuantity: itemData.maxOrderQuantity,
      currentCartItems: cart.items,
      requestedQuantity: quantity,
    });

    // Check maxQuantity limits BEFORE adding to cart
    const currentCartQuantity = getItemCartQuantity(itemData.id);
    const finalQuantity = quantity < (itemData.minOrderQuantity || 1) ? itemData.minOrderQuantity || 1 : quantity;
    const newTotalQuantity = currentCartQuantity + finalQuantity;
    
    console.log("🔢 Quantity calculation:", {
      currentCartQuantity,
      finalQuantity,
      newTotalQuantity,
    });
    
    if (!canAddMoreToCart(itemData, newTotalQuantity)) {
      const stockLimit = (itemData.stockQuantity && itemData.stockQuantity > 0) ? itemData.stockQuantity : Infinity;
      const maxOrderLimit = (itemData.maxOrderQuantity && itemData.maxOrderQuantity > 0) ? itemData.maxOrderQuantity : Infinity;
      const maxQuantity = Math.min(stockLimit, maxOrderLimit);
      console.log("❌ canAddMoreToCart returned false:", {
        stockLimit,
        maxOrderLimit,
        maxQuantity,
        check: `${newTotalQuantity} < ${maxQuantity} = ${newTotalQuantity < maxQuantity}`,
      });
      setSnackbar({
        open: true,
        message: `Maximum quantity (${maxQuantity === Infinity ? 'unlimited' : maxQuantity}) reached for this item`,
      });
      return;
    }

    // Check if the item has size or color options that need to be selected
    if (
      (itemData.sizes && itemData.sizes.length > 0) ||
      (itemData.colors && itemData.colors.length > 0)
    ) {
      // If the item has options but they haven't been selected yet,
      // open the modal to allow selection first
      if (!itemData.selectedSize && !itemData.selectedColor) {
        setSelectedItemForModal(itemData);
        setModalOpen(true);
        return;
      }
    }

    try {
      setCartLoading(true);

      const result = await firebaseCartService.addItemToCart(
        user.uid,
        itemData,
        finalQuantity
      );

      if (result.success) {
        setSnackbar({ open: true, message: result.message });
        // Silently add to cart - don't open cart drawer
        // Cart will be updated via real-time listener
      } else {
        setSnackbar({ open: true, message: result.error });
      }
    } catch (error) {
      console.error("❌ Error adding item to cart:", error);
      setSnackbar({ open: true, message: "Failed to add item to cart" });
    } finally {
      setCartLoading(false);
    }
  };

  // Enhanced handleItemClick function for Buy Now
  const handleBuyNow = (itemData) => {
    // Check if the item has size or color options that need to be selected
    if (
      (itemData.sizes && itemData.sizes.length > 0) ||
      (itemData.colors && itemData.colors.length > 0)
    ) {
      if (!itemData.selectedSize && !itemData.selectedColor) {
        setSelectedItemForModal(itemData);
        setModalOpen(true);
        return;
      }
    }

    // Navigate to CartPage
    setShowCart(true);
  };

  const removeFromCart = async (type, id) => {
    if (!user?.uid) return;

    try {
      setCartLoading(true);
      const result = await firebaseCartService.removeFromCart(
        user.uid,
        id,
        type
      );

      if (!result.success) {
        setSnackbar({ open: true, message: result.error });
      }
      // Cart will be updated via real-time listener
    } catch (error) {
      console.error("❌ Error removing from cart:", error);
      setSnackbar({ open: true, message: "Failed to remove item from cart" });
    } finally {
      setCartLoading(false);
    }
  };

  const updateCartQuantity = async (type, id, newQuantity) => {
    if (!user?.uid) return;

    try {
      setCartLoading(true);
      const result = await firebaseCartService.updateItemQuantity(
        user.uid,
        id,
        newQuantity,
        type
      );

      if (!result.success) {
        setSnackbar({ open: true, message: result.error });
      }
      // Cart will be updated via real-time listener
    } catch (error) {
      console.error("❌ Error updating cart quantity:", error);
      setSnackbar({ open: true, message: "Failed to update cart" });
    } finally {
      setCartLoading(false);
    }
  };

  const getCartItemCount = () => {
    return (
      cart.packages.reduce((sum, p) => sum + p.quantity, 0) +
      cart.items.reduce((sum, i) => sum + i.quantity, 0)
    );
  };

  // GST calculation function for sub-items - GST is calculated on BASE PRICE ONLY
  const calculateSubItemGST = (subItem) => {
    const packagePrice = subItem.itemPrice || 0; // This is the selling/package price (base + profit)
    const basePrice = subItem.basePrice || 0; // Use basePrice for GST calculation
    const profitMargin = subItem.profitMargin || 0; // Profit portion
    const gstPercentage = (subItem.gstPercentage !== undefined && subItem.gstPercentage !== null && subItem.gstPercentage !== '') ? Number(subItem.gstPercentage) : 18;
    
    // GST should only be calculated on the base price, not on profit margin
    const gstAmount = basePrice > 0 ? (basePrice * gstPercentage) / 100 : 0;
    
    // Final price = base price + profit + GST (on base price only)
    const totalWithGST = basePrice + profitMargin + gstAmount;
    
    return {
      basePrice: basePrice,
      profitMargin: profitMargin,
      packagePrice: packagePrice,
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      totalWithGST: totalWithGST
    };
  };

  const getCartTotal = () => {
    const packageTotal = cart.packages.reduce(
      (sum, pkg) => {
        // For packages with sub-items, calculate GST properly
        if (pkg.hasSubItems && pkg.selectedSubItems && Object.keys(pkg.selectedSubItems).length > 0) {
          let subItemsTotal = 0;
          Object.entries(pkg.selectedSubItems).forEach(([subItemId, isSelected]) => {
            if (isSelected) {
              const subItem = pkg.subItems?.find(item => item.id.toString() === subItemId);
              if (subItem) {
                const quantity = pkg.subItemQuantities?.[subItemId] || 1;
                const gstInfo = calculateSubItemGST(subItem);
                subItemsTotal += gstInfo.totalWithGST * quantity;
              }
            }
          });
          return sum + (subItemsTotal * pkg.quantity);
        } 
        // For packages with sub-items that use selectedSubItemsList (array format)
        else if (pkg.isSubItemPackage && pkg.selectedSubItemsList && pkg.selectedSubItemsList.length > 0) {
          let subItemsTotal = 0;
          pkg.selectedSubItemsList.forEach((subItem) => {
            const quantity = subItem.selectedQuantity || 1;
            const gstInfo = calculateSubItemGST(subItem);
            subItemsTotal += gstInfo.totalWithGST * quantity;
          });
          return sum + (subItemsTotal * pkg.quantity);
        }
        // Regular packages WITHOUT sub-items - NO GST, use price as-is
        else {
          return sum + pkg.price * pkg.quantity;
        }
      },
      0
    );
    const itemTotal = cart.items.reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    return packageTotal + itemTotal;
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "" });
  };

  const canAddMoreToCart = (item, requestedTotalQuantity) => {
    // Only use stockQuantity as limit if it's a positive number
    const stockLimit = (item.stockQuantity && item.stockQuantity > 0) ? item.stockQuantity : Infinity;
    const maxOrderLimit = (item.maxOrderQuantity && item.maxOrderQuantity > 0) ? item.maxOrderQuantity : Infinity;
    const maxQuantity = Math.min(stockLimit, maxOrderLimit);
    
    console.log("📊 canAddMoreToCart check:", {
      itemStockQuantity: item.stockQuantity,
      itemMaxOrderQuantity: item.maxOrderQuantity,
      stockLimit,
      maxOrderLimit,
      maxQuantity,
      requestedTotalQuantity,
      result: requestedTotalQuantity <= maxQuantity,
    });
    
    // Use <= instead of < to allow adding up to the max quantity
    return requestedTotalQuantity <= maxQuantity;
  };

  const getItemCartQuantity = (itemId) => {
    const cartItem = cart.items.find((i) => i.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(ALL_CATEGORIES_LABEL);
    setDisplayedItemsCount(8);
  };

  // Lazy loading function
  const loadMoreItems = () => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedItemsCount((prev) => prev + 8);
      setIsLoadingMore(false);
    }, 500);
  };

  // Scroll handler for lazy loading
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
        document.documentElement.offsetHeight
      ) {
        return;
      }

      const filteredItems = getFilteredItems();
      if (displayedItemsCount < filteredItems.length) {
        loadMoreItems();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [displayedItemsCount]);

  // Reset displayed items count when search/filter changes
  useEffect(() => {
    setDisplayedItemsCount(8);
  }, [searchQuery, selectedCategory]);

  const checkMilestones = async (profile = userProfile) => {
    if (!user?.uid || !profile) return;
    try {
      const pending = await checkMilestonePopupPending(user.uid, profile);
      if (!pending) return;

      const details = getMilestoneDetails(pending.milestone);
      if (!details) return;

      await markMilestonePopupShown(user.uid, pending.milestone);

      if (pending.needsEmail) {
        await notifyMilestoneAchievement(profile, pending.milestone);
        await markMilestoneEmailSent(user.uid, pending.milestone);
      }

      setMilestoneData(details);
      setShowMilestonePopup(true);
    } catch (error) {
      console.error("Error checking milestones:", error);
    }
  };

  const handleMilestonePopupClose = () => {
    setShowMilestonePopup(false);
    setMilestoneData(null);
  };

  const fetchUserProfile = async () => {
    try {
      console.log("=== FETCHING USER PROFILE ===");
      console.log("Querying for UID:", user.uid);

      const userDoc = await getDocs(
        query(collection(db, "users"), where("uid", "==", user.uid))
      );

      if (userDoc.empty) {
        console.log("❌ User authenticated but not found in database");
        setError("Your profile was not found. Please sign up again.");
        setTimeout(() => {
          handleSignOut();
        }, 3000);
        return;
      }

      const userDocSnap = userDoc.docs[0];
      const userData = userDocSnap.data();

      if (!userData.registrationFee) {
        const registrationFeeData = {
          amount: 499,
          paid: false,
          paidAt: null,
          confirmedByAdmin: false,
          confirmedAt: null,
        };

        await updateDoc(doc(db, "users", userDocSnap.id), {
          registrationFee: registrationFeeData,
        });

        userData.registrationFee = registrationFeeData;
      }

      setUserProfile(userData);

      if (userData.hasPurchasedPackage) {
        setTimeout(() => checkMilestones(userData), 2000);
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      setError("Failed to load your profile. Please try signing in again.");
    }
  };

  useEffect(() => {
    if (!user?.uid) {
      setUserProfile(null);
      return;
    }

    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", user.uid)
    );

    const unsubscribe = onSnapshot(
      userQuery,
      async (snapshot) => {
        if (snapshot.empty) {
          console.log("❌ User authenticated but not found in database");
          setError("Your profile was not found. Please sign up again.");
          setTimeout(() => {
            handleSignOut();
          }, 3000);
          return;
        }

        const userDocSnap = snapshot.docs[0];
        let userData = userDocSnap.data();

        if (!userData.registrationFee) {
          const registrationFeeData = {
            amount: 499,
            paid: false,
            paidAt: null,
            confirmedByAdmin: false,
            confirmedAt: null,
          };

          await updateDoc(doc(db, "users", userDocSnap.id), {
            registrationFee: registrationFeeData,
          });

          userData = { ...userData, registrationFee: registrationFeeData };
        }

        setUserProfile(userData);
      },
      (error) => {
        console.error("Failed to listen to user profile:", error);
        setError("Failed to load your profile. Please try signing in again.");
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Fetch active packages
  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const packagesQuery = query(
        collection(db, "packages"),
        where("isActive", "==", true)
      );
      const snapshot = await getDocs(packagesQuery);
      const packagesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPackages(packagesData);
    } catch (error) {
      console.error("Failed to fetch packages:", error);
    } finally {
      setPackagesLoading(false);
    }
  };

  // Fetch active items
  const fetchItems = async () => {
    try {
      setItemsLoading(true);
      const itemsQuery = query(
        collection(db, "items"),
        where("isActive", "==", true)
      );
      const snapshot = await getDocs(itemsQuery);
      const itemsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setItems(itemsData);

      console.log("Items fetched:", itemsData.length);
    } catch (error) {
      console.error("Failed to fetch items:", error);
    } finally {
      setItemsLoading(false);
    }
  };

  // Filter items based on search and category
  const getFilteredItems = () => {
    let filtered = items;

    // Filter by category
    if (selectedCategory !== ALL_CATEGORIES_LABEL) {
      filtered = filtered.filter((item) =>
        categoryMatchesFilter(item.category, selectedCategory)
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.description &&
            item.description.toLowerCase().includes(query)) ||
          (item.brand && item.brand.toLowerCase().includes(query)) ||
          (item.category && item.category.toLowerCase().includes(query))
      );
    }

    // Shuffle the filtered results every time
    return shuffleArray(filtered);
  };

  const filteredItems = getFilteredItems();

  const itemCountByCategory = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      if (item.category) {
        const key = canonicalCategoryName(item.category);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [items]);

  const categoryImageOverrides = useMemo(() => {
    const images = {};
    items.forEach((item) => {
      if (!item.category || !item.imageUrl) return;
      const key = canonicalCategoryName(item.category);
      if (!images[key]) images[key] = item.imageUrl;
    });
    return images;
  }, [items]);

  const showCategoryComingSoon =
    !itemsLoading &&
    selectedCategory !== ALL_CATEGORIES_LABEL &&
    !searchQuery.trim() &&
    filteredItems.length === 0;

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setDisplayedItemsCount(8);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const handleBackFromOrders = () => {
    setShowMyOrders(false);
  };

  const handleBackFromProfileSettings = () => {
    setShowProfileSettings(false);
    fetchUserProfile();
  };

  const handlePaymentComplete = (paymentData) => {
    alert(
      `Payment recorded! Order: ${
        paymentData.packageName
      } - Total: ${formatPrice(
        paymentData.totalAmount
      )}\nYour payment was successful and your order is confirmed.`
    );
    setShowCheckout(false);
    setSelectedPackage(null);
  };

  const handleBackToPackages = () => {
    setShowCheckout(false);
    setSelectedPackage(null);
  };

  const handleBackFromPackageStore = () => {
    setShowPackageStore(false);
  };

  const handleBackFromItemCheckout = () => {
    setShowItemCheckout(false);
    setSelectedItem(null);
  };

  const handleBackFromCart = () => {
    setShowCart(false);
  };

  // Ultra Customer Popup handlers
  const handleCloseUltraPopup = () => {
    setShowUltraPopup(false);
    // Remember dismissal for 24 hours
    localStorage.setItem("ultraPopupDismissed", Date.now().toString());
  };

  const handleUltraPurchase = () => {
    setShowUltraPopup(false);
    // Navigate to package store to purchase activation package
    setShowPackageStore(true);
  };

  const handleItemPurchaseComplete = (purchaseData) => {
    alert(
      `Order placed successfully! Order ID: ${purchaseData.orderId}\nItem: ${
        purchaseData.itemName
      } × ${purchaseData.quantity}\nTotal: ${formatPrice(
        purchaseData.totalAmount
      )}\nYour payment was successful and your order is confirmed.`
    );
    setShowItemCheckout(false);
    setSelectedItem(null);
  };

  const handleCartCheckout = async (cartData) => {
    alert(
      `Cart order placed successfully! Total: ${formatPrice(
        cartData.totalAmount
      )}\nYour payment was successful and your order is confirmed.`
    );
    setShowCart(false);

    // Clear cart in Firebase after successful checkout
    if (user?.uid) {
      try {
        await firebaseCartService.clearCart(user.uid);
        console.log("✅ Cart cleared after successful checkout");
      } catch (error) {
        console.error("❌ Error clearing cart after checkout:", error);
      }
    }
  };

  // Clean up Firebase cart service when user signs out
  const handleSignOut = async () => {
    try {
      // Clean up cart listeners before signing out
      if (user?.uid) {
        firebaseCartService.removeCartListener(user.uid);
      }

      // Clear URL parameters to prevent referral loop after sign out
      const url = new URL(window.location);
      url.searchParams.delete("ref");
      url.searchParams.delete("auth_intent");
      url.searchParams.delete("item_id");
      window.history.replaceState({}, document.title, url.pathname);

      // Clear any pending cart items or session data
      sessionStorage.removeItem("pendingCartItem");
      // Clear any localStorage cart data
      if (user?.uid) {
        localStorage.removeItem(`infinitywhale_cart_${user.uid}`);
      }

      await signOut(auth);
      onSignOut();
      handleClose();
      window.location.reload();
    } catch (error) {
      setError("Failed to sign out: " + error.message);
    }
  };

  // Handle item request submission
  const handleRequestItem = async (itemName) => {
    try {
      if (!user || !userProfile) {
        setError("Please sign in to request items");
        return;
      }

      // Check for duplicate requests
      const duplicateCheck = await ItemRequestService.checkDuplicateRequest(
        user.email,
        itemName
      );

      if (!duplicateCheck.success) {
        setError("Failed to check for duplicate requests");
        return;
      }

      if (duplicateCheck.hasDuplicate) {
        setError("You have already requested this item. Please wait for admin response.");
        return;
      }

      // Submit the request
      const result = await ItemRequestService.submitItemRequest({
        itemName,
        userEmail: user.email,
        userId: user.uid,
        userName: userProfile.fullName || userProfile.displayName || "Anonymous User",
      });

      if (result.success) {
        setSuccess("Item request submitted successfully! Admin will review your request.");
      } else {
        setError(result.error || "Failed to submit item request");
      }
    } catch (error) {
      console.error("Error submitting item request:", error);
      setError("Failed to submit item request: " + error.message);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchItems();
  }, [user.uid]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    console.log("=== USER PROFILE STATE CHANGED ===");
    console.log("userProfile:", userProfile);
    console.log(
      "hasPurchasedPackage from state:",
      userProfile?.hasPurchasedPackage
    );
    console.log("cashbackBalance from state:", userProfile?.cashbackBalance);
    console.log("================================");

    // Show Ultra Customer popup for non-Ultra users (after a short delay)
    if (userProfile && !userProfile.hasPurchasedPackage && !isAdmin) {
      // Check if popup was dismissed recently (within 24 hours)
      const lastDismissed = localStorage.getItem("ultraPopupDismissed");
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      
      if (!lastDismissed || (now - parseInt(lastDismissed)) > twentyFourHours) {
        // Show popup after 3 seconds
        const timer = setTimeout(() => {
          setShowUltraPopup(true);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [userProfile, isAdmin]);

  useEffect(() => {
    // Check for pending cart item from ItemSharePage
    const handlePendingCartItem = () => {
      const pendingItem = sessionStorage.getItem("pendingCartItem");
      if (pendingItem && user && userProfile) {
        try {
          const itemData = JSON.parse(pendingItem);
          console.log("🛒 PROCESSING PENDING CART ITEM:", itemData);

          // Clear the pending item first to prevent loops
          sessionStorage.removeItem("pendingCartItem");

          // Check if item was added recently (within last 5 minutes)
          const timeElapsed = Date.now() - (itemData.shareTimestamp || 0);
          if (timeElapsed > 5 * 60 * 1000) {
            // 5 minutes
            console.log("❌ Pending cart item expired");
            return;
          }

          // Add item to cart
          if (itemData.type === "item") {
            // Create the item object in the expected format
            const itemToAdd = {
              id: itemData.id,
              name: itemData.name,
              sellingPrice: itemData.sellingPrice,
              description: itemData.description,
              category: itemData.category,
              stockQuantity: itemData.stockQuantity,
              minOrderQuantity: itemData.minOrderQuantity || 1,
              maxOrderQuantity: itemData.maxOrderQuantity || 10,
              imageUrl: itemData.imageUrl,
              sizes: itemData.sizes,
              colors: itemData.colors,
              // Add selected variants if they exist
              selectedSize: itemData.selectedVariants?.selectedSize,
              selectedColor: itemData.selectedVariants?.selectedColor,
            };

            // Add to cart with the specified quantity
            addItemToCart(itemToAdd, itemData.quantity);

            // Show success message
            setSnackbar({
              open: true,
              message: `🎉 "${itemData.name}" has been added to your cart!`,
            });

            console.log("✅ Successfully added shared item to cart");
          }
        } catch (error) {
          console.error("❌ Error processing pending cart item:", error);
          sessionStorage.removeItem("pendingCartItem"); // Clean up on error
        }
      }
    };

    // Check for auth intent in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const authIntent = urlParams.get("auth_intent");
    const itemId = urlParams.get("item_id");

    if (authIntent === "add_to_cart" && itemId) {
      console.log("🔍 Detected auth intent for add_to_cart:", itemId);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Handle pending cart item after user is authenticated and profile is loaded
    handlePendingCartItem();
  }, [user, userProfile, addItemToCart, setSnackbar]); // Dependencies to run when authentication state changes

  // ALSO ADD THIS USEEFFECT TO CHECK URL PARAMETERS ON COMPONENT MOUNT
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authIntent = urlParams.get("auth_intent");

    if (authIntent === "add_to_cart") {
      console.log("🔍 User needs authentication for add to cart action");
      // The actual cart addition will happen after authentication completes
    }
  }, []); // Run once on mount

  const handleMenu = (event) => {
    // If user is on any page other than home, clicking profile icon should go back to home
    if (showProfileSettings || showMyOrders || showPackageStore || showWallet) {
      // Go back to home page
      setShowProfileSettings(false);
      setShowMyOrders(false);
      setShowPackageStore(false);
      setShowWallet(false);
      setShowCheckout(false);
      setSelectedPackage(null);
    } else {
      // Normal behavior: open menu dropdown
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: getBrandPageBackground(primaryBlue),
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(11, 28, 72, 0.08) 0%, transparent 50%)
          `,
          zIndex: 0,
          pointerEvents: "none",
        },
      }}
    >
      {showCheckout && selectedPackage ? (
        <CheckoutPage
          packageData={selectedPackage}
          userProfile={userProfile}
          isAdmin={isAdmin}
          user={user}
          onBack={handleBackToPackages}
          onPaymentComplete={handlePaymentComplete}
        />
      ) : showItemCheckout && selectedItem ? (
        <ItemCheckoutPage
          itemData={selectedItem}
          userProfile={userProfile}
          isAdmin={isAdmin}
          user={user}
          onBack={handleBackFromItemCheckout}
          onPurchaseComplete={handleItemPurchaseComplete}
        />
      ) : showCart ? (
        <CartPage
          cart={cart}
          userProfile={userProfile}
          isAdmin={isAdmin}
          user={user}
          onBack={handleBackFromCart}
          onUpdateQuantity={updateCartQuantity}
          onRemoveItem={removeFromCart}
          onCheckoutComplete={handleCartCheckout}
        />
      ) : showMyOrders ? (
        <MyOrders user={user} onBack={handleBackFromOrders} userProfile={userProfile} />
      ) : showProfileSettings ? (
        <ProfileSettings
          user={user}
          userProfile={userProfile}
          onBack={handleBackFromProfileSettings}
          setShowPackageStore={setShowPackageStore}
        />
      ) : showWallet ? (
        <WalletView
          user={user}
          userProfile={userProfile}
          onBack={() => setShowWallet(false)}
          onRefreshProfile={fetchUserProfile}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />
      ) : showPackageStore ? (
        <PackageStore
          user={user}
          userProfile={userProfile}
          packages={packages}
          packagesLoading={packagesLoading}
          onBack={handleBackFromPackageStore}
          onPackageSelect={(pkg) => {
            setSelectedPackage(pkg);
            setShowCheckout(true);
            setShowPackageStore(false);
          }}
          onRefreshProfile={fetchUserProfile}
        />
      ) : (
        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* NavBar Component */}
          <NavBar
            user={user}
            handleMenu={handleMenu}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            cartItemCount={getCartItemCount()}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
            setCartDrawerOpen={setCartDrawerOpen}
            userProfile={userProfile}
          />

          <CategoryIconStrip
            selectedCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
            itemCountByCategory={itemCountByCategory}
            categoryImageOverrides={categoryImageOverrides}
            primaryBlue={primaryBlue}
          />

          {/* FloatingCartButton Component */}
          <FloatingCartButton
            getCartItemCount={getCartItemCount}
            setCartDrawerOpen={setCartDrawerOpen}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
          />

          {/* CartDrawer Component */}
          <CartDrawer
            cartDrawerOpen={cartDrawerOpen}
            handleCartDrawerClose={handleCartDrawerClose}
            cart={cart}
            getCartItemCount={getCartItemCount}
            getCartTotal={getCartTotal}
            removeFromCart={removeFromCart}
            updateCartQuantity={updateCartQuantity}
            canAddMoreToCart={canAddMoreToCart}
            formatPrice={formatPrice}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
            cardGlassStyles={cardGlassStyles}
            setShowCart={setShowCart}
            setCartDrawerOpen={setCartDrawerOpen}
          />

          {/* Notifications Component */}
          <Notifications
            snackbar={snackbar}
            handleSnackbarClose={handleSnackbarClose}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
          />

          {/* UserMenu Component */}
          <UserMenu
            anchorEl={anchorEl}
            handleClose={handleClose}
            isAdmin={isAdmin}
            onAdminPanel={onAdminPanel}
            handleSignOut={handleSignOut}
            setShowMyOrders={setShowMyOrders}
            setShowPackageStore={setShowPackageStore}
            setCartDrawerOpen={setCartDrawerOpen}
            setShowProfileSettings={setShowProfileSettings}
            getCartItemCount={getCartItemCount}
            setDeleteDialogOpen={setDeleteDialogOpen}
            deleteDialogOpen={deleteDialogOpen}
            loading={loading}
            primaryBlue={primaryBlue}
            glassStyles={glassStyles}
            setShowWallet={setShowWallet}
          />

          {/* Main Content */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {error && (
              <Container maxWidth="lg" sx={{ pt: 2 }}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    ...glassStyles,
                    bgcolor: "rgba(244, 67, 54, 0.9)",
                    color: "white",
                    fontWeight: 600,
                  }}
                >
                  {error}
                </Alert>
              </Container>
            )}

            {success && (
              <Container maxWidth="lg" sx={{ pt: error ? 0 : 2 }}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 2,
                    ...glassStyles,
                    bgcolor: "rgba(76, 175, 80, 0.9)",
                    color: "white",
                    fontWeight: 600,
                  }}
                >
                  {success}
                </Alert>
              </Container>
            )}

            <Container maxWidth="lg" sx={{ py: 4 }}>
              {showCategoryComingSoon ? (
                <CategoryComingSoonPanel
                  categoryName={selectedCategory}
                  primaryBlue={primaryBlue}
                  glassStyles={glassStyles}
                  onBrowseAll={() => handleCategorySelect(ALL_CATEGORIES_LABEL)}
                  onNotifyInterest={(name) => handleRequestItem(name)}
                />
              ) : (
                <ProductGrid
                  itemsLoading={itemsLoading}
                  items={items}
                  filteredItems={filteredItems}
                  displayedItemsCount={displayedItemsCount}
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  handleClearFilters={handleClearFilters}
                  formatPrice={formatPrice}
                  getItemCartQuantity={getItemCartQuantity}
                  cartItems={cart.items}
                  addItemToCart={addItemToCart}
                  setSelectedItem={setSelectedItem}
                  setShowItemCheckout={setShowItemCheckout}
                  isLoadingMore={isLoadingMore}
                  loadMoreItems={loadMoreItems}
                  primaryBlue={primaryBlue}
                  glassStyles={glassStyles}
                  cardGlassStyles={cardGlassStyles}
                  handleBuyNow={handleBuyNow}
                  modalOpen={modalOpen}
                  setModalOpen={setModalOpen}
                  selectedItemForModal={selectedItemForModal}
                  setSelectedItemForModal={setSelectedItemForModal}
                  setShowCart={setShowCart}
                  onRequestItem={handleRequestItem}
                />
              )}
            </Container>

            {/* Footer Component */}
            <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />
          </Box>

          {/* Ultra Customer Popup for non-Ultra users */}
          <UltraCustomerPopup
            open={showUltraPopup}
            onClose={handleCloseUltraPopup}
            onPurchase={handleUltraPurchase}
            userProfile={userProfile}
          />

          <MilestoneRewardPopup
            open={showMilestonePopup}
            onClose={handleMilestonePopupClose}
            milestoneData={milestoneData}
          />
        </Box>
      )}
    </Box>
  );
};

export default HomePage;
